import {
  AFFORDABILITY_CHECKING,
  AFFORDABILITY_UNKNOWN,
  OwnerAffordability,
} from '@lib/utils/ownerAffordability';
import { simulateLibraryIngest } from '@stores/Billing';
import { useEffect, useRef, useState } from 'react';

/** How long the selection has to hold still before we ask. */
const PROBE_DEBOUNCE_MS = 400;

interface UseOwnerAffordabilityOptions {
  /** The library the media is going into. Undefined while the folder store is still loading. */
  libraryId: string | undefined;
  /**
   * `IngestEstimate.durationSeconds` of the *non-duplicate* selection. Not the credit-floored
   * length: see `simulateLibraryIngest`. 0 when unselected.
   */
  ingestSeconds: number;
  /** Set only for a cross-billed library; `billingStore` already answers the self-billed case. */
  enabled: boolean;
}

/**
 * Pre-flight check for uploads into a library somebody else pays for. See
 * `ownerAffordability.ts` for what the verdict means.
 *
 * `requestKey` stamps which (library, duration) pair the stored verdict answers, so deselecting
 * from a 3-hour selection down to 30 seconds cannot leave the 3-hour shortfall on screen
 * (`isSettledForCurrentRequest` in `dataframe-absence.md`). It drops out-of-order responses for
 * free.
 *
 * The key binds a verdict to the *question*, not to the probe: returning to a question already
 * answered shows the earlier verdict while the new probe runs, rather than flickering every gate
 * through `checking` on each checkbox toggle.
 */
export function useOwnerAffordability({
  libraryId,
  ingestSeconds,
  enabled,
}: UseOwnerAffordabilityOptions): OwnerAffordability {
  // Whole seconds: a duration summed from floats churns the key on identical selections.
  const seconds = Math.max(0, Math.ceil(ingestSeconds));
  const requestKey = enabled && libraryId && seconds > 0 ? `${libraryId}:${seconds}` : null;

  const [answer, setAnswer] = useState<{ key: string; result: OwnerAffordability } | null>(null);

  // The effect's own cleanup runs on dep change *and* unmount, so it is the unmount cleanup
  // `.claude/rules/timer-cleanup.md` asks for; a second unmount-only effect would find nothing.
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (requestKey === null) return;

    const controller = new AbortController();
    let cancelled = false;

    timer.current = setTimeout(() => {
      timer.current = null;
      // Non-null here: `requestKey` is only non-null when `libraryId` is.
      simulateLibraryIngest(libraryId as string, seconds, { signal: controller.signal }).then(
        (result) => {
          if (cancelled) return;
          setAnswer({ key: requestKey, result });
        },
      );
    }, PROBE_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      controller.abort();
      if (timer.current) {
        clearTimeout(timer.current);
        timer.current = null;
      }
    };
  }, [requestKey, libraryId, seconds]);

  if (requestKey === null) return AFFORDABILITY_UNKNOWN;
  return answer?.key === requestKey ? answer.result : AFFORDABILITY_CHECKING;
}
